import { defineStore } from 'pinia'
import Network from '@/Network'
import {
  HandoverStatusFilter,
  ExpirationBucket,
  ListApprovalHistoryParams,
  ListPendingApprovalsParams,
  PackageNature
} from '@/Network/Types/Requests/HandoverInviteRequests'
import {
  AdminQueueResponse,
  ApprovePreviewResponse,
  PaginationMeta
} from '@/Network/Types/Responses/HandoverInviteResponses'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'
import { extractConsecutiveHoursViolations } from '@/utils/consecutiveHoursErrors'
import { useToast } from 'vue-toastification'

export type HandoverFilters = {
  query: string
  scheduleId?: number
  healthUnitId?: number
  nature?: PackageNature
  startDate?: string
  endDate?: string
  expirationStartDate?: string
  expirationEndDate?: string
  operationalGroups?: string[]
  expirationBucket?: ExpirationBucket
  statuses?: HandoverStatusFilter[]
}

export type HandoverHistoryFilters = {
  query: string
  scheduleId?: number
  healthUnitId?: number
  startDate?: string
  endDate?: string
  expirationStartDate?: string
  expirationEndDate?: string
  operationalGroups?: string[]
  statuses?: HandoverStatusFilter[]
}

/**
 * Default do context "pending": pré-seleciona o status que o backend já usa
 * implicitamente quando `statuses[]` não é enviado (fila de aprovação). Sem
 * isso o chip de Status aparece vazio mesmo com a tela filtrada — o usuário
 * não tem como saber que só está vendo IN_ADMIN_QUEUE.
 */
const DEFAULT_PENDING_STATUSES: HandoverStatusFilter[] = [HandoverStatusFilter.IN_ADMIN_QUEUE]

/**
 * Semântica "todos" para quando o usuário desmarca manualmente todas as
 * opções do filtro de Status no context pending: enviar os valores
 * explicitamente, em vez de omitir `statuses[]` — que cairia no default
 * implícito do backend (IN_ADMIN_QUEUE) e pareceria que "limpar" não
 * funcionou.
 *
 * Derivado do enum, e não escrito à mão: como lista paralela, ela silenciava
 * justamente o status que tivesse acabado de ser criado — "todos" passaria a
 * significar "todos menos o mais novo", sem erro de tipo nem teste quebrado.
 */
const ALL_PENDING_STATUSES: HandoverStatusFilter[] = Object.values(HandoverStatusFilter)

export type HandoverState = {
  pendingApprovals: AdminQueueResponse[]
  meta: PaginationMeta
  filters: HandoverFilters
  isLoading: boolean
  currentEvaluation: AdminQueueResponse | null
  isLoadingEvaluation: boolean
  isProcessing: boolean
  history: AdminQueueResponse[]
  historyMeta: PaginationMeta
  isLoadingHistory: boolean
  historyFilters: HandoverHistoryFilters
  /** Violações de horas consecutivas devolvidas pelo 400 da última tentativa de aprovação. */
  approvalConsecutiveHoursViolations: ConsecutiveHoursViolation[]
}

export const useHandoverStore = defineStore('handover', {
  state: (): HandoverState => ({
    pendingApprovals: [],
    meta: {
      page: 1,
      perPage: 10,
      total: 0,
      totalPages: 0
    },
    filters: {
      query: '',
      statuses: [...DEFAULT_PENDING_STATUSES]
    },
    isLoading: false,
    currentEvaluation: null,
    isLoadingEvaluation: false,
    isProcessing: false,
    history: [],
    historyMeta: {
      page: 1,
      perPage: 10,
      total: 0,
      totalPages: 0
    },
    isLoadingHistory: false,
    historyFilters: {
      query: ''
    },
    approvalConsecutiveHoursViolations: []
  }),
  getters: {
    totalPending: (state) => state.meta.total
  },
  actions: {
    async fetchPendingApprovals(page?: number) {
      const toast = useToast()
      try {
        this.isLoading = true
        const params: ListPendingApprovalsParams = {
          query: this.filters.query || undefined,
          scheduleId: this.filters.scheduleId,
          healthUnitId: this.filters.healthUnitId,
          nature: this.filters.nature,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          expirationStartDate: this.filters.expirationStartDate,
          expirationEndDate: this.filters.expirationEndDate,
          operationalGroups: this.filters.operationalGroups?.length
            ? this.filters.operationalGroups
            : undefined,
          expirationBucket: this.filters.expirationBucket,
          // Seleção vazia = usuário desmarcou tudo -> "todos os status"
          // explícito. Não pode virar `undefined`: isso ativaria o default
          // implícito do backend (IN_ADMIN_QUEUE) e o filtro pareceria travado.
          statuses: this.filters.statuses?.length ? this.filters.statuses : ALL_PENDING_STATUSES,
          page: page ?? this.meta.page,
          perPage: this.meta.perPage
        }
        const response = await Network.handoverInvite.listPendingApprovals(params)
        this.pendingApprovals = response.data
        this.meta = response.meta
      } catch (error) {
        toast.error('Erro ao buscar aprovações pendentes')
        console.error(error)
        this.pendingApprovals = []
      } finally {
        this.isLoading = false
      }
    },
    async adminApprove(packageId: number, cancellationReason?: string) {
      const toast = useToast()
      try {
        await Network.handoverInvite.adminApprove(packageId, cancellationReason)
        toast.success('Pacote aprovado com sucesso')
        await this.fetchPendingApprovals()
      } catch (error) {
        toast.error('Erro ao aprovar o pacote')
        console.error(error)
      }
    },
    async adminDecline(packageId: number) {
      const toast = useToast()
      try {
        const result = await Network.handoverInvite.adminDecline(packageId)
        if (result.hasPendingCandidates) {
          toast.success('Candidato recusado. Aguardando resposta dos demais candidatos.')
        } else {
          toast.success('Candidato recusado. Nenhum outro candidato na fila.')
        }
        await this.fetchPendingApprovals()
      } catch (error) {
        toast.error('Erro ao recusar o candidato')
        console.error(error)
      }
    },
    setCurrentEvaluation(item: AdminQueueResponse | null) {
      this.currentEvaluation = item
    },
    async fetchAdminQueueItem(packageId: number) {
      const toast = useToast()
      try {
        this.isLoadingEvaluation = true
        const item = await Network.handoverInvite.getAdminQueueItem(packageId)
        this.currentEvaluation = item
      } catch (error) {
        toast.error('Erro ao buscar detalhes da aprovação')
        console.error(error)
        this.currentEvaluation = null
      } finally {
        this.isLoadingEvaluation = false
      }
    },
    async fetchApprovePreview(packageId: number): Promise<ApprovePreviewResponse | null> {
      const toast = useToast()
      try {
        return await Network.handoverInvite.getApprovePreview(packageId)
      } catch (error) {
        toast.error('Erro ao verificar conflitos')
        console.error(error)
        return null
      }
    },
    async adminApproveFromEvaluation(
      packageId: number,
      cancellationReason?: string
    ): Promise<boolean> {
      const toast = useToast()
      try {
        this.isProcessing = true
        this.approvalConsecutiveHoursViolations = []
        await Network.handoverInvite.adminApprove(packageId, cancellationReason)
        toast.success('Troca aprovada com sucesso')
        this.currentEvaluation = null
        return true
      } catch (error) {
        // O bloqueio de horas consecutivas vem com as violações no corpo do 400: guarda-as
        // para a tela exibir o motivo e a data de liberação. O toast genérico só sobra para
        // falhas sem esse detalhe — senão ele esconderia a explicação real do usuário.
        const violations = extractConsecutiveHoursViolations(error)
        if (violations) {
          this.approvalConsecutiveHoursViolations = violations
        } else {
          toast.error('Erro ao aprovar a troca')
        }
        console.error(error)
        return false
      } finally {
        this.isProcessing = false
      }
    },
    async adminDeclineFromEvaluation(packageId: number): Promise<AdminQueueResponse | null> {
      const toast = useToast()
      try {
        this.isProcessing = true
        const result = await Network.handoverInvite.adminDecline(packageId)

        if (result.hasPendingCandidates) {
          toast.success('Candidato recusado. Aguardando resposta dos demais candidatos.')
          this.currentEvaluation = null
          return null
        }

        try {
          const updated = await Network.handoverInvite.getAdminQueueItem(packageId)
          this.currentEvaluation = updated
          toast.success('Candidato recusado. Próximo candidato carregado.')
          return updated
        } catch {
          toast.success('Candidato recusado. Nenhum outro candidato na fila.')
          this.currentEvaluation = null
          return null
        }
      } catch (error) {
        toast.error('Erro ao recusar o candidato')
        console.error(error)
        return null
      } finally {
        this.isProcessing = false
      }
    },
    async fetchHistory(page?: number) {
      const toast = useToast()
      try {
        this.isLoadingHistory = true
        const params: ListApprovalHistoryParams = {
          query: this.historyFilters.query || undefined,
          scheduleId: this.historyFilters.scheduleId,
          healthUnitId: this.historyFilters.healthUnitId,
          startDate: this.historyFilters.startDate,
          endDate: this.historyFilters.endDate,
          expirationStartDate: this.historyFilters.expirationStartDate,
          expirationEndDate: this.historyFilters.expirationEndDate,
          operationalGroups: this.historyFilters.operationalGroups?.length
            ? this.historyFilters.operationalGroups
            : undefined,
          statuses: this.historyFilters.statuses?.length ? this.historyFilters.statuses : undefined,
          page: page ?? this.historyMeta.page,
          perPage: this.historyMeta.perPage
        }
        const response = await Network.handoverInvite.listApprovalHistory(params)
        this.history = response.data
        this.historyMeta = response.meta
      } catch (error) {
        toast.error('Erro ao buscar histórico de trocas')
        console.error(error)
        this.history = []
      } finally {
        this.isLoadingHistory = false
      }
    },
    setFilters(filters: Partial<HandoverFilters>) {
      this.filters = { ...this.filters, ...filters }
    },
    resetFilters() {
      this.filters = { query: '', statuses: [...DEFAULT_PENDING_STATUSES] }
      this.meta.page = 1
    },
    setHistoryFilters(filters: Partial<HandoverHistoryFilters>) {
      this.historyFilters = { ...this.historyFilters, ...filters }
    },
    resetHistoryFilters() {
      this.historyFilters = { query: '' }
      this.historyMeta.page = 1
    }
  }
})
